/**
 * GreenVertus — Cross-sell v21 (25/09/2026)
 *
 * 1. TIROIR PANIER : les cartes sont rendues côté serveur (snippets/cart-enhancements.liquid,
 *    position 'cross-sell' + gv-cs-card / gv-cs-eval). Ce fichier gère seulement :
 *    - le changement de grammage (prix + message de palier caché si le grammage ne suffit plus),
 *    - l'ajout au panier ([data-gv-cs2-add]) avec la propriété cachée _gv_src=cs-drawer.
 *    Le tiroir est re-rendu par le thème (cart:update + morph), donc les suggestions se
 *    recalculent toutes seules après chaque ajout.
 *
 * 2. FICHE PRODUIT (« Souvent achetés ensemble », .gv-cs[data-product-id]) : toujours en JS,
 *    recommandations 'complementary' puis repli 'related'. Corrigé en v21 :
 *    - produits épuisés ignorés, 1re variante DISPONIBLE présélectionnée (avant : variants[0],
 *      d'où des cartes « Épuisé » pour des produits disponibles en 50 g / 100 g),
 *    - seules les variantes disponibles dans le menu, suffixe « g » sur les grammages numériques,
 *    - plus de navigation au clic sur toute la carte (image et nom seulement),
 *    - ajout marqué _gv_src=cs-product.
 *
 * 3. Pont de compatibilité anciens widgets "Elite" (inchangé).
 *
 * Mesure : les propriétés qui commencent par "_" ne s'affichent pas au client mais restent sur
 * la ligne de commande -> CA généré par source = lignes de commande avec _gv_src.
 */
(function () {
  'use strict';

  const state = { cartIds: [], ready: false };
  const recoCache = {};
  let _ignoreNextCartUpdate = false;

  function money(cents) {
    return (cents / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
  }

  function variantLabel(title) {
    return /^\d+([.,]\d+)?$/.test(String(title).trim()) ? title + ' g' : title;
  }

  function escapeHtml(s) {
    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  async function fetchCart() {
    const r = await fetch('/cart.js', { cache: 'no-store' });
    if (!r.ok) throw new Error('cart.js ' + r.status);
    const cart = await r.json();
    state.cartIds = cart.items.map(i => i.product_id);
    return cart;
  }

  // Rafraîchit le tiroir après un ajout ; sans tiroir (page /cart), recharge la page.
  async function refreshDrawer(addData, sourceId) {
    const comp = document.querySelector('cart-items-component[data-drawer]');
    const sectionId = comp && comp.dataset ? comp.dataset.sectionId : null;
    if (!sectionId) { window.location.reload(); return; }
    const cart = await fetchCart();
    const sres = await fetch('/?sections=' + sectionId);
    if (!sres.ok) throw new Error('sections ' + sres.status);
    const sections = await sres.json();
    _ignoreNextCartUpdate = true;
    document.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail: { resource: cart, sourceId: sourceId, data: { sections: sections, itemCount: cart.item_count } },
    }));
    setTimeout(renderAll, 350);
  }

  async function addLine(variantId, src) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1, properties: { _gv_src: src } }] }),
    });
    if (!res.ok) throw new Error('cart/add ' + res.status);
    return res.json();
  }

  /* ── 1. Tiroir : cartes rendues en Liquid ───────────────────────────────── */

  document.addEventListener('change', function (e) {
    const sel = e.target.closest && e.target.closest('.gv-cs2__select');
    if (!sel) return;
    const card = sel.closest('[data-gv-cs2-card]');
    if (!card) return;
    const opt = sel.options[sel.selectedIndex];
    const btn = card.querySelector('[data-gv-cs2-add]');
    const price = card.querySelector('.gv-cs2__price');
    if (btn) btn.dataset.variantId = opt.value;
    if (price) price.textContent = opt.dataset.price + ' €';
    const hint = card.querySelector('.gv-cs2__hint');
    if (hint && card.dataset.ecart) {
      hint.hidden = parseInt(opt.dataset.cents, 10) < parseInt(card.dataset.ecart, 10);
    }
  });

  document.addEventListener('click', async function (e) {
    const btn = e.target.closest && e.target.closest('[data-gv-cs2-add]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    if (btn.hasAttribute('data-loading')) return;
    const label = btn.textContent;
    btn.setAttribute('data-loading', '');
    btn.textContent = 'Ajout…';
    try {
      const addData = await addLine(btn.dataset.variantId, 'cs-drawer');
      btn.removeAttribute('data-loading');
      btn.setAttribute('data-added', '');
      btn.textContent = '✓ Ajouté';
      await refreshDrawer(addData, 'gv-crosssell');
    } catch (err) {
      console.warn('[GV CS] ajout tiroir impossible', err);
      btn.removeAttribute('data-loading');
      btn.textContent = label;
    }
  }, true);

  /* ── 2. Fiche produit : « Souvent achetés ensemble » (JS) ───────────────── */

  async function fetchRecoFor(pid, intent) {
    const key = pid + ':' + intent;
    if (recoCache[key]) return recoCache[key];
    let products = [];
    try {
      const r = await fetch(`/recommendations/products.json?product_id=${pid}&limit=10&intent=${intent}`);
      if (r.ok) products = (await r.json()).products || [];
    } catch (e) { }
    if (!products.length && intent === 'complementary') {
      return fetchRecoFor(pid, 'related');
    }
    recoCache[key] = products;
    return products;
  }

  function firstAvailable(product) {
    return (product.variants || []).find(v => v.available) || null;
  }

  function buildCard(product) {
    const v = firstAvailable(product);
    const title = escapeHtml(product.title);
    const raw = product.featured_image || '';
    const imgUrl = raw ? (raw.includes('?') ? raw + '&width=400' : raw + '?width=400') : null;
    const avail = (product.variants || []).filter(x => x.available);

    function promoHtml(variant) {
      const c = variant.compare_at_price;
      if (!c || c <= variant.price) return '';
      const pct = Math.round((1 - variant.price / c) * 100);
      return `<span class="gv-cs__compare">${money(c)}</span><span class="gv-cs__savings">−${pct} %</span>`;
    }

    let variantHtml = '';
    if (avail.length > 1 || (avail.length === 1 && avail[0].title !== 'Default Title')) {
      variantHtml = `
        <div class="gv-cs__variant-wrap">
          <select class="gv-cs__variant-select" aria-label="Choisir le grammage de ${title}">
            ${avail.map(x => `<option value="${x.id}" data-price="${x.price}" data-compare="${x.compare_at_price || ''}"${x.id === v.id ? ' selected' : ''}>${escapeHtml(variantLabel(x.title))}</option>`).join('')}
          </select>
          <svg aria-hidden="true" focusable="false" class="gv-cs__variant-icon" viewBox="0 0 10 6"><path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor"></path></svg>
        </div>`;
    }

    const card = document.createElement('article');
    card.className = 'gv-cs__card';
    card.setAttribute('role', 'listitem');
    card.innerHTML = [
      `<a href="${product.url}" class="gv-cs__img-wrap" tabindex="-1" aria-hidden="true">`,
      imgUrl ? `<img class="gv-cs__img" src="${imgUrl}" alt="" loading="lazy" width="400" height="400">` : `<div class="gv-cs__img-placeholder">🌿</div>`,
      '</a>',
      '<div class="gv-cs__body">',
      `<a href="${product.url}" class="gv-cs__name">${title}</a>`,
      `<div class="gv-cs__price-row"><span class="gv-cs__price">${money(v.price)}</span>${promoHtml(v)}</div>`,
      variantHtml,
      `<button class="gv-cs__cta" type="button" data-variant-id="${v.id}" aria-label="Ajouter ${title} au panier"><span>Ajouter au panier</span></button>`,
      '</div>',
    ].join('');

    card.querySelector('.gv-cs__cta').addEventListener('click', e => {
      e.preventDefault();
      e.stopPropagation();
      addFromProductPage(e.currentTarget, product.id);
    });

    const select = card.querySelector('.gv-cs__variant-select');
    if (select) {
      select.addEventListener('change', () => {
        const opt = select.options[select.selectedIndex];
        const btn = card.querySelector('.gv-cs__cta');
        btn.dataset.variantId = opt.value;
        const variant = avail.find(x => String(x.id) === opt.value);
        card.querySelector('.gv-cs__price-row').innerHTML =
          `<span class="gv-cs__price">${money(variant.price)}</span>${promoHtml(variant)}`;
      });
    }
    return card;
  }

  function showSkeleton(track) {
    if (!track.querySelector('.gv-cs__skeleton-card')) {
      track.innerHTML = '<div class="gv-cs__skeleton">' + '<div class="gv-cs__skeleton-card"></div>'.repeat(4) + '</div>';
    }
  }

  async function renderContainer(container) {
    const track = container.querySelector('.gv-cs__track');
    if (!track) return;
    const pid = container.dataset.productId;
    if (!pid) return;
    const intent = container.dataset.intent || 'related';

    if (!state.ready) { showSkeleton(track); return; }

    const token = pid + '|' + intent + '|' + state.cartIds.join(',');
    if (container.dataset.gvToken === token && track.querySelector('.gv-cs__card')) return;
    container.dataset.gvToken = token;

    const products = await fetchRecoFor(pid, intent);
    if (container.dataset.gvToken !== token) return;

    const selfId = parseInt(pid, 10);
    const filtered = products
      .filter(p => p.id !== selfId && !state.cartIds.includes(p.id) && firstAvailable(p))
      .slice(0, 4);

    if (!filtered.length) { container.style.display = 'none'; return; }

    container.style.display = '';
    track.innerHTML = '';
    track.style.opacity = '0';
    track.style.transition = 'opacity .25s ease';
    filtered.forEach(p => track.appendChild(buildCard(p)));
    requestAnimationFrame(() => { track.style.opacity = '1'; });
  }

  function renderAll() {
    document.querySelectorAll('.gv-cs[data-product-id]').forEach(c => { renderContainer(c); });
  }

  const adding = new Set();

  async function addFromProductPage(btn, productId) {
    const variantId = btn.dataset.variantId;
    if (adding.has(variantId)) return;
    adding.add(variantId);
    btn.setAttribute('data-loading', '');
    btn.innerHTML = '<span>Ajout…</span>';
    try {
      const addData = await addLine(variantId, 'cs-product');
      if (!state.cartIds.includes(productId)) state.cartIds.push(productId);
      btn.removeAttribute('data-loading');
      btn.setAttribute('data-added', '');
      btn.innerHTML = '<span>✓ Ajouté</span>';
      await refreshDrawer(addData, 'gv-crosssell');
    } catch (err) {
      console.warn('[GV CS] ajout fiche produit impossible', err);
      btn.removeAttribute('data-loading');
      btn.innerHTML = '<span>Ajouter au panier</span>';
    } finally {
      adding.delete(variantId);
    }
  }

  async function refreshAndRender() {
    try {
      await fetchCart();
      state.ready = true;
      renderAll();
    } catch (e) { console.warn('[GV CS] refresh', e); }
  }

  window.gvCsRenderAll = renderAll;
  window.gvCsInit = refreshAndRender;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refreshAndRender);
  } else {
    refreshAndRender();
  }

  document.addEventListener('cart:update', async () => {
    if (_ignoreNextCartUpdate) {
      _ignoreNextCartUpdate = false;
      setTimeout(renderAll, 350);
      return;
    }
    try {
      await fetchCart();
      state.ready = true;
      setTimeout(renderAll, 350);
    } catch (e) { }
  });

  /* ── 3. Pont de compatibilité anciens widgets Elite ────────────────────── */
  let _lastLegacyBridge = 0;
  async function legacyBridge(e) {
    if (e && e.detail && (e.detail.sourceId === 'gv-legacy-bridge' || e.detail.sourceId === 'gv-crosssell')) return;
    if (Date.now() - _lastLegacyBridge < 600) return;
    _lastLegacyBridge = Date.now();
    try {
      const cart = await fetchCart();
      state.ready = true;
      const comp = document.querySelector('cart-items-component[data-drawer]');
      const sectionId = comp && comp.dataset ? comp.dataset.sectionId : null;
      const detailData = { itemCount: cart.item_count };
      if (sectionId) {
        const sres = await fetch(`/?sections=${sectionId}`);
        if (sres.ok) detailData.sections = await sres.json();
      }
      _ignoreNextCartUpdate = true;
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: { resource: cart, sourceId: 'gv-legacy-bridge', data: detailData },
      }));
      setTimeout(renderAll, 350);
    } catch (e) { }
  }
  document.addEventListener('cart:updated', legacyBridge);
  document.addEventListener('cart:refresh', legacyBridge);

  new MutationObserver(mutations => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (node.matches?.('.gv-cs[data-product-id]') || node.querySelector?.('.gv-cs[data-product-id]')) {
          setTimeout(renderAll, 100);
          return;
        }
      }
    }
  }).observe(document.body, { childList: true, subtree: true });
})();
